import { useCallback, useEffect, useRef } from 'react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import ViewpointsPanel from '@viewer/viewpoints'

const MIN_W = 360
const MIN_H = 320
const MAX_W = 1200
const MAX_H = 1400

// Floating-window mode of the viewpoints panel: same content, drag
// + resize, persisted geometry. Mirrors the DetachedRightSidebar but
// keyed on the viewpoint slice's own state.
function DetachedViewpointsPanel() {
  const detached = useAppStore(
    (s) => s.viewpointsPanelOpen && s.viewpointsPanelMode === 'floating',
  )
  const rect = useAppStore((s) => s.viewpointsPanelFloating)
  const setRect = useAppStore((s) => s.setViewpointsPanelFloating)
  const setMode = useAppStore((s) => s.setViewpointsPanelMode)
  const { z, trazer } = useJanelaAberta('viewpoints-destacados', detached)
  const dragOriginRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)
  const resizeOriginRef = useRef<{
    pointerStart: { x: number; y: number }
    sizeStart: { w: number; h: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragOriginRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { x: rect.x, y: rect.y },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [rect.x, rect.y],
  )

  const onResizeDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      resizeOriginRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        sizeStart: { w: rect.w, h: rect.h },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [rect.w, rect.h],
  )

  useEffect(() => {
    if (!detached) return
    const onMove = (e: PointerEvent) => {
      if (dragOriginRef.current) {
        const { pointerStart, posStart } = dragOriginRef.current
        const dx = e.clientX - pointerStart.x
        const dy = e.clientY - pointerStart.y
        const x = Math.min(
          Math.max(0, posStart.x + dx),
          window.innerWidth - 80,
        )
        const y = Math.min(
          Math.max(0, posStart.y + dy),
          window.innerHeight - 40,
        )
        setRect({ ...rect, x, y })
      }
      if (resizeOriginRef.current) {
        const { pointerStart, sizeStart } = resizeOriginRef.current
        const dx = e.clientX - pointerStart.x
        const dy = e.clientY - pointerStart.y
        const w = Math.min(MAX_W, Math.max(MIN_W, sizeStart.w + dx))
        const h = Math.min(MAX_H, Math.max(MIN_H, sizeStart.h + dy))
        setRect({ ...rect, w, h })
      }
    }
    const onUp = () => {
      dragOriginRef.current = null
      resizeOriginRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [detached, rect, setRect])

  if (!detached) return null

  return (
    <div
      role="dialog"
      aria-label="Viewpoints (destacado)"
      onPointerDownCapture={trazer}
      className="fixed flex flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl"
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        zIndex: z,
      }}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex h-7 shrink-0 cursor-move items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs text-[var(--color-text)] select-none"
      >
        <span className="truncate">Viewpoints (destacado)</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setMode('window')}
            title="Mover para janela do browser"
            className="rounded px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            ↗ janela
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setMode('fixed')}
            title="Voltar a anexar à barra lateral"
            className="rounded px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            ↘ anexar
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <ViewpointsPanel />
      </div>
      <div
        onPointerDown={onResizeDown}
        className="absolute bottom-0 right-0 h-3 w-3 cursor-se-resize"
        style={{
          backgroundImage:
            'linear-gradient(135deg, transparent 50%, var(--color-text-muted) 50%)',
          opacity: 0.6,
        }}
        title="Redimensionar"
      />
    </div>
  )
}

export default DetachedViewpointsPanel
